import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { setReturnMethod, ReturnMethod } from '@/redux/return/return-slice'
import { useTheme } from '@/ui/theme'
import type { ReturnMethodOption } from '@itsrever/returns-api-types'
import DeliveryPickup from './delivery_pickup.svg'
import Dropoff from './dropoff.svg'
import HomePickup from './homepickup.svg'
import InStore from './instore.svg'
import SelfShipping from './selfshipping.svg'
import { formatCurrency, toRGB } from '@/utils'
import { useTranslate } from '@/hooks'
import clsx from 'clsx'
import { LeafIcon } from '@/ui/icons/leaf'
import { SearchIcon } from '@/ui/icons/search'
import { ShieldCheckIcon } from '@/ui/icons/shield-check'
import { useTranslation } from 'react-i18next'
import { WarehouseAddress } from '@/ui/components/warehouse-address'

interface ReturnCardProps {
  method: ReturnMethodOption
}

const ReturnCard: React.FC<ReturnCardProps> = ({ method }) => {
  const { translate } = useTranslate()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const returnMethod = useAppSelector((store) => store.return.returnMethod)
  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const methodName = method.name
  const selected = returnMethod === methodName

  let id: string | undefined
  if (methodName !== undefined) {
    id = methodName.toString()
  }

  const iconSource = () => {
    switch (methodName) {
      case ReturnMethod.CollectionPoint:
        return Dropoff
      case ReturnMethod.DeliveryPickup:
        return DeliveryPickup
      case ReturnMethod.HomePickup:
        return HomePickup
      case ReturnMethod.InStore:
        return InStore
      case ReturnMethod.SelfShipping:
        return SelfShipping
    }
  }

  const greenOption = methodName === ReturnMethod.InStore
  const isSelfShipping = methodName === ReturnMethod.SelfShipping

  const showButton =
    methodName === ReturnMethod.InStore ||
    methodName === ReturnMethod.CollectionPoint

  const href = () => {
    if (methodName === ReturnMethod.CollectionPoint) {
      return method.drop_off_point_url
    } else {
      return settings.store_list_url
    }
  }
  const linkLabel =
    showButton && href()
      ? translate(
          methodName === ReturnMethod.InStore
            ? 'return_card_in_store_link'
            : 'return_card_dropoff_link'
        )
      : ''
  // Merchants hide the button by emptying its copy, not only by clearing the URL.
  const showLink = linkLabel.trim() !== ''
  const isFree = parseInt(method.costs?.amount || '0') === 0
  const price = formatCurrency(
    method.costs?.amount || '0',
    method.costs?.currency || 'EUR'
  )
  const originalAmount = method.original_costs?.amount
  const originalPrice =
    originalAmount &&
    Number(originalAmount) > Number(method.costs?.amount || '0')
      ? formatCurrency(originalAmount, method.original_costs?.currency || 'EUR')
      : undefined
  const priceLabel = (
    <>
      {originalPrice ? (
        <>
          <s className="font-normal opacity-60">{originalPrice}</s>{' '}
        </>
      ) : null}
      {price}
    </>
  )

  const isMisako = settings.ecommerce_group == 'misako'
  const isHoff = settings.ecommerce_group == 'hoff'

  if (isHoff) {
    return (
      <div
        id={id}
        className="relative flex flex-col content-center items-center border-b-1 border-solid border-black"
        onClick={() =>
          dispatch(
            setReturnMethod({
              returnMethod: methodName ?? ReturnMethod.NoReturnMethod,
              userSelectedCarrier: method.carrier ?? '',
              userSelectedProvider: method.provider ?? '',
              warehouseAddress: method.warehouse_address
            })
          )
        }
      >
        {greenOption ? (
          <div className="absolute left-4 top-[-10px] flex items-center justify-center gap-1 rounded-[20px] border border-[#20904d] py-0.5 pl-4 pr-3 text-white [background:radial-gradient(circle_at_78px_10px,#20904d,#4caf50_100%)]">
            <LeafIcon className="size-4" />
            <p className="text-xs">
              <b
                dangerouslySetInnerHTML={{
                  __html: translate('return_card_green_option')
                }}
              />
            </p>
          </div>
        ) : null}
        {isFree && method.costs && !originalPrice ? (
          <div
            className="absolute right-0 top-0 flex h-[25px] justify-center rounded-bl-[70px] rounded-tr border-b border-solid py-0.5 pl-3 pr-1.5"
            style={{
              color: greenOption ? 'white' : theme.colors.primary.contrastText,
              borderBottomColor: greenOption
                ? '#20904d'
                : theme.colors.primary.main,
              backgroundColor: greenOption
                ? '#20904d'
                : theme.colors.primary.main
            }}
          >
            <p
              className="whitespace-nowrap text-xs"
              dangerouslySetInnerHTML={{
                __html: translate('return_card_free')
              }}
            />
          </div>
        ) : null}
        <div
          className={clsx(
            'w-full cursor-pointer border-t-1 border-solid border-black px-4 py-3 hover:bg-neutral-100',
            selected && 'bg-neutral-100'
          )}
        >
          <div className="w-full">
            <div className="mb-2 flex items-center gap-2">
              <span>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="currentColor"
                    d="M10.115 21.811c.606.5 1.238.957 1.885 1.403a27 27 0 0 0 1.885-1.403a28 28 0 0 0 2.853-2.699C18.782 16.877 21 13.637 21 10a9 9 0 1 0-18 0c0 3.637 2.218 6.876 4.262 9.112a28 28 0 0 0 2.853 2.7M12 13.25a3.25 3.25 0 1 1 0-6.5a3.25 3.25 0 0 1 0 6.5"
                  />
                </svg>
              </span>
              <p className="text-lg">
                <span
                  className="font-light"
                  dangerouslySetInnerHTML={{
                    __html: translate(`return_card_method${methodName}_title`)
                  }}
                />
              </p>
            </div>
            {method.costs ? (
              <p className="text-sm font-light text-neutral-500">
                {translate('return_shipping_cost')}: {priceLabel}
              </p>
            ) : null}
            <p
              className="font-normal"
              style={{
                color: selected
                  ? theme.colors.common.black
                  : theme.colors.grey[0]
              }}
              dangerouslySetInnerHTML={{
                __html: translate(`return_card_method${methodName}_description`)
              }}
            />
            {isSelfShipping ? (
              <WarehouseAddress
                address={method.warehouse_address}
                className="mt-3"
              />
            ) : null}
          </div>
          {showLink ? (
            <div className="mt-4 flex">
              <a
                target="_blank"
                href={href()}
                rel="noreferrer"
                className="flex items-center gap-2 text-black underline-offset-2 transition-all hover:underline"
              >
                <span>
                  <svg
                    aria-hidden="true"
                    fill="none"
                    focusable="false"
                    width="24"
                    viewBox="0 0 24 24"
                    className="size-4"
                  >
                    <path
                      d="M10.364 3a7.364 7.364 0 1 0 0 14.727 7.364 7.364 0 0 0 0-14.727Z"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeMiterlimit="10"
                    ></path>
                    <path
                      d="M15.857 15.858 21 21.001"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                    ></path>
                  </svg>
                </span>
                <p
                  className="text-xs"
                  dangerouslySetInnerHTML={{
                    __html: linkLabel
                  }}
                />
              </a>
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  if (isMisako) {
    return (
      <div
        id={id}
        className="relative flex w-full flex-1 xl:max-w-64"
        onClick={() =>
          dispatch(
            setReturnMethod({
              returnMethod: methodName as unknown as ReturnMethod,
              userSelectedCarrier: method.carrier ?? '',
              userSelectedProvider: method.provider ?? '',
              warehouseAddress: method.warehouse_address
            })
          )
        }
      >
        <div className="mb-1 flex-1">
          <div
            className={clsx(
              'flex size-full flex-1 cursor-pointer flex-col rounded-sm border border-solid border-neutral-300 p-4 transition-all hover:border-neutral-400 hover:bg-neutral-100/60',
              { 'border-neutral-400 bg-neutral-100/60': selected }
            )}
          >
            <div className="w-full">
              <div className="mb-1 flex justify-between gap-2">
                <p
                  className="mt-0.5 text-[15px] font-[300] tracking-wider"
                  dangerouslySetInnerHTML={{
                    __html: translate(`return_card_method${methodName}_title`)
                  }}
                />
                {methodName === 4 ? (
                  <svg
                    width="22px"
                    height="22px"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    color="#000000"
                  >
                    <path
                      d="M16 9.2C16 13.1765 9 20 9 20C9 20 2 13.1765 2 9.2C2 5.22355 5.13401 2 9 2C12.866 2 16 5.22355 16 9.2Z"
                      stroke="#000000"
                      strokeWidth="1.5"
                    ></path>
                    <path
                      d="M16 19L22 19"
                      stroke="#000000"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                    <path
                      d="M9 10C9.55228 10 10 9.55228 10 9C10 8.44772 9.55228 8 9 8C8.44772 8 8 8.44772 8 9C8 9.55228 8.44772 10 9 10Z"
                      fill="#000000"
                      stroke="#000000"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                ) : null}
              </div>
              {method.costs ? (
                <p className="mb-2 mt-3 flex items-center gap-1 text-xs font-[300] tracking-wider text-neutral-400">
                  {translate('return_shipping_cost')}: {priceLabel}
                </p>
              ) : null}
              <p
                className="text-[13px] font-[300] leading-5 tracking-wider"
                style={{
                  color: selected
                    ? theme.colors.common.black
                    : theme.colors.grey[0]
                }}
                dangerouslySetInnerHTML={{
                  __html: translate(
                    `return_card_method${methodName}_description`
                  )
                }}
              />
              {isSelfShipping ? (
                <WarehouseAddress
                  address={method.warehouse_address}
                  className="mt-3"
                />
              ) : null}
            </div>
            {showLink ? (
              <div className="mt-auto flex justify-center pt-4">
                <a
                  target="_blank"
                  href={href()}
                  rel="noreferrer"
                  className="flex items-center gap-1.5 rounded-none px-2.5 py-1.5 underline-offset-8 hover:underline"
                  style={{
                    backgroundColor: 'transparent',
                    color: '#262626'
                  }}
                >
                  <p
                    className="text-center text-[10px] leading-snug tracking-wider"
                    dangerouslySetInnerHTML={{
                      __html: linkLabel
                    }}
                  />
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      id={id}
      className="relative flex flex-col content-center items-center"
      onClick={() =>
        dispatch(
          setReturnMethod({
            returnMethod: methodName as unknown as ReturnMethod,
            userSelectedCarrier: method.carrier ?? '',
            userSelectedProvider: method.provider ?? '',
            warehouseAddress: method.warehouse_address
          })
        )
      }
    >
      {greenOption ? (
        <div className="absolute left-4 top-[-10px] flex items-center justify-center gap-1 rounded-[20px] border border-[#20904d] py-0.5 pl-4 pr-3 text-white [background:radial-gradient(circle_at_78px_10px,#20904d,#4caf50_100%)]">
          <LeafIcon className="size-4" />
          <p className="text-xs">
            <b
              dangerouslySetInnerHTML={{
                __html: translate('return_card_green_option')
              }}
            />
          </p>
        </div>
      ) : null}
      {isFree && method.costs && !originalPrice ? (
        <div
          className="absolute right-0 top-0 flex h-[25px] justify-center rounded-bl-[70px] rounded-tr border-b border-solid py-0.5 pl-3 pr-1.5"
          style={{
            color: greenOption ? 'white' : theme.colors.primary.contrastText,
            borderBottomColor: greenOption
              ? '#20904d'
              : theme.colors.primary.main,
            backgroundColor: greenOption ? '#20904d' : theme.colors.primary.main
          }}
        >
          <p
            className="whitespace-nowrap text-xs font-medium"
            dangerouslySetInnerHTML={{
              __html: translate('return_card_free')
            }}
          />
        </div>
      ) : null}
      <div className="mb-1 flex w-full items-center">
        <div
          className="mb-6 w-full cursor-pointer rounded"
          style={{
            padding: selected ? '12px 10px' : '14px 12px',
            borderWidth: selected ? 2 : 0,
            borderStyle: 'solid',
            borderColor: selected
              ? greenOption
                ? '#20904d'
                : theme.colors.primary.main
              : 'transparent',
            backgroundColor: selected
              ? greenOption
                ? 'rgb(0, 117, 0, 0.1)'
                : toRGB(theme.colors.primary.main)
              : 'transparent',
            boxShadow: selected ? 'none' : '0px 0px 2px 2px rgb(0 0 0 / 0.1)'
          }}
        >
          <div className="w-full">
            <div className="flex items-center gap-2">
              <img
                src={iconSource()}
                alt=""
                className={`size-[15px] ${selected ? 'animate-spin-icon' : ''}`}
              />
              <p className="text-lg">
                <b
                  dangerouslySetInnerHTML={{
                    __html: translate(`return_card_method${methodName}_title`)
                  }}
                />
              </p>
            </div>
            {method.costs ? (
              <p className="pb-1 font-semibold text-orange-400">
                {translate('return_shipping_cost')}: {priceLabel}
              </p>
            ) : null}
            {originalPrice ? (
              <p className="flex items-center gap-1 pb-1 text-sm text-neutral-800">
                <ShieldCheckIcon className="size-4 shrink-0" />
                {t('return_card.cover_free', {
                  ecommerceName: settings.branding?.title,
                  interpolation: { escapeValue: false }
                })}
              </p>
            ) : null}
            <p
              className="font-normal"
              style={{
                color: selected
                  ? theme.colors.common.black
                  : theme.colors.grey[0]
              }}
              dangerouslySetInnerHTML={{
                __html: translate(`return_card_method${methodName}_description`)
              }}
            />
            {isSelfShipping ? (
              <WarehouseAddress
                address={method.warehouse_address}
                className="mt-3"
              />
            ) : null}
          </div>
          {showLink ? (
            <div className="mt-4 flex justify-center">
              <a
                target="_blank"
                href={href()}
                rel="noreferrer"
                className="flex items-center gap-1.5 rounded-[20px] px-2 py-0.5 hover:underline"
                style={{
                  backgroundColor: greenOption
                    ? '#20904d'
                    : theme.colors.primary.main,
                  color: greenOption
                    ? 'white'
                    : theme.colors.primary.contrastText
                }}
              >
                <SearchIcon className="size-3.5" />
                <p
                  className="text-xs font-medium"
                  dangerouslySetInnerHTML={{
                    __html: linkLabel
                  }}
                />
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default ReturnCard
